'use client';

/**
 * PropertyCategoryIcon - inline line-art ikony kategorii nehnutelnosti.
 * Nahradza staticke /images/filter/filter_ico_*.svg (biely glyph v sedom kruhu),
 * ktore sa nedali farbit ani animovat.
 *
 * Vsetky glyphy: viewBox 24, stroke currentColor, 1.6 (round cap/join)
 * -> jednotna optika s lucide ikonami v zvysku UI.
 */
import { cn } from '@/shadcn/lib/utils';
import { ListingPropertyCategory } from '@/features/listings/definitions/propertyTypes';

type Props = { className?: string };

const S = {
    fill: 'none' as const,
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
};

function Svg({ className, children }: Props & { children: React.ReactNode }) {
    return (
        <svg viewBox="0 0 24 24" className={cn(className)} aria-hidden {...S}>
            {children}
        </svg>
    );
}

/* Domy - rodinny dom so sedlovou strechou a komínom. */
export function CatHouseIcon(props: Props) {
    return (
        <Svg {...props}>
            <path d="M2.9 11.3 12 3.7l9.1 7.6" />
            <path d="M5.2 10.1v9.2a1 1 0 0 0 1 1h11.6a1 1 0 0 0 1-1v-9.2" />
            <path d="M9.7 20.3v-5.1h4.6v5.1" />
            <path d="M16.6 6.6V4.5h2.2v3.9" />
        </Svg>
    );
}

/* Byty - bytovy dom, dva bloky + okna. */
export function CatApartmentIcon(props: Props) {
    return (
        <Svg {...props}>
            <path d="M4 20.4V8.2a1 1 0 0 1 1-1h5.6" />
            <path d="M10.6 20.4V4.6a1 1 0 0 1 1-1h7.4a1 1 0 0 1 1 1v15.8" />
            <path d="M2.6 20.4h18.8" />
            <path d="M13.6 7.2h1.1M16.9 7.2H18M13.6 11h1.1M16.9 11H18M13.6 14.8h1.1M16.9 14.8H18" />
            <path d="M6.5 11h1.6M6.5 14.8h1.6" />
        </Svg>
    );
}

/* Pozemky - parcela v perspektive s deliacou ciarou (vytycena plocha). */
export function CatLandIcon(props: Props) {
    return (
        <Svg {...props}>
            <path d="M12 5.4 22 12l-10 6.6L2 12l10-6.6Z" />
            <path d="M6.6 8.5 16.6 15.1" />
        </Svg>
    );
}

/* Rekreacne - A-frame chata. */
export function CatRecreationalIcon(props: Props) {
    return (
        <Svg {...props}>
            <path d="M3.4 20.4 12 3.6l8.6 16.8H3.4Z" />
            <path d="M9.4 20.4v-5.2h5.2v5.2" />
            <path d="M7.2 13.2h9.6" />
        </Svg>
    );
}

/* Ostatne - komercna prevadzka: markiza + vyklad + dvere. */
export function CatOtherIcon(props: Props) {
    return (
        <Svg {...props}>
            <path d="M4.9 4.6h14.2l2.3 4.8H2.6l2.3-4.8Z" />
            <path d="M4.2 9.4v10a1 1 0 0 0 1 1h13.6a1 1 0 0 0 1-1v-10" />
            <path d="M13.4 20.4v-5.2h4.2v5.2" />
            <path d="M6.4 12.6h4.4v4.2H6.4z" />
        </Svg>
    );
}

export const PROPERTY_CATEGORY_ICONS: Record<string, (p: Props) => React.ReactElement> = {
    [ListingPropertyCategory.HOUSE]: CatHouseIcon,
    [ListingPropertyCategory.APARTMENT]: CatApartmentIcon,
    [ListingPropertyCategory.LAND]: CatLandIcon,
    [ListingPropertyCategory.RECREATIONAL]: CatRecreationalIcon,
    [ListingPropertyCategory.OTHER]: CatOtherIcon,
};

export function PropertyCategoryIcon({
    categoryKey,
    className,
}: {
    categoryKey: string;
    className?: string;
}) {
    const Icon = PROPERTY_CATEGORY_ICONS[categoryKey] ?? CatOtherIcon;
    return <Icon className={className} />;
}
